import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import AppContext from '../contexts/AppContext';
import TimeRangeSelector from '../components/analysis/TimeRangeSelector';
import RecentChangesDashboard from '../components/analysis/RecentChangesDashboard';
import TsVenueStatTiles from '../components/analysis/TsVenueStatTiles';
import TsVenueTrendDashboard from '../components/analysis/TsVenueTrendDashboard';
import TsVenueTopContentDashboard from '../components/analysis/TsVenueTopContentDashboard';
import TsVenueModuleBreakdownDashboard from '../components/analysis/TsVenueModuleBreakdownDashboard';
import TsVenueBannerPerformanceDashboard from '../components/analysis/TsVenueBannerPerformanceDashboard';
import { hasElevatedSessionAccess } from '../utils/sessionPermissions';

const SectionCard = ({ children, className = '' }) => (
  <div className={`rounded-[28px] border border-[#e2e7f6] bg-white p-5 shadow-[0_18px_42px_rgba(15,23,42,0.05)] ${className}`}>
    {children}
  </div>
);

const TsVenueAnalytics = () => {
  const { selectedRole, token, userInfo } = useContext(AppContext);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(yesterday);
  sevenDaysAgo.setDate(yesterday.getDate() - 6);

  const [timeRange, setTimeRange] = useState('7');
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(yesterday);
  const [summary, setSummary] = useState({
    trend: [],
    topContent: [],
    moduleBreakdown: [],
    bannerBreakdown: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const roleArray = userInfo?.roleInfo?.[selectedRole?.type] || [];
  const currentRole = roleArray.find((role) => role.id === selectedRole?.id);
  const canShowAdvanced = hasElevatedSessionAccess(userInfo, currentRole?.permissionLevel);

  useEffect(() => {
    let ignore = false;

    const fetchSummary = async () => {
      if (!selectedRole?.id || selectedRole?.type !== 'tsVenue' || !token) {
        if (!ignore) {
          setSummary({ trend: [], topContent: [], moduleBreakdown: [], bannerBreakdown: [] });
          setError('');
          setLoading(false);
        }
        return;
      }

      try {
        if (!ignore) {
          setLoading(true);
          setError('');
        }
        const localStart = new Date(startDate);
        const localEnd = new Date(endDate);
        localStart.setHours(0, 0, 0, 0);
        localEnd.setHours(23, 59, 59, 999);

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/analysis/ts-venue/read/summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            venue_id: selectedRole.id,
            startDate: dayjs(localStart).format('YYYY-MM-DDTHH:mm:ss.SSS'),
            endDate: dayjs(localEnd).format('YYYY-MM-DDTHH:mm:ss.SSS'),
            userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result?.error || 'Failed to fetch touchscreen analytics');
        }

        if (!ignore) {
          setSummary({
            trend: Array.isArray(result?.trend) ? result.trend : [],
            topContent: Array.isArray(result?.topContent) ? result.topContent : [],
            moduleBreakdown: Array.isArray(result?.moduleBreakdown) ? result.moduleBreakdown : [],
            bannerBreakdown: Array.isArray(result?.bannerBreakdown) ? result.bannerBreakdown : [],
          });
          setError('');
        }
      } catch (error) {
        console.error('Failed to fetch touchscreen analytics:', error);
        if (!ignore) {
          setSummary({ trend: [], topContent: [], moduleBreakdown: [], bannerBreakdown: [] });
          setError(error instanceof Error ? error.message : 'Failed to fetch touchscreen analytics');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchSummary();
    return () => {
      ignore = true;
    };
  }, [startDate, endDate, selectedRole?.id, selectedRole?.type, token]);

  return (
    <div className="mx-auto flex w-[90%] max-w-7xl flex-col gap-6 pb-8">
      <SectionCard>
        <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-[1.2rem] font-semibold text-[#1f2d4d]">Touchscreen Overview</h2>
            <p className="mt-1 text-sm text-[#6b7894]">Monitor kiosk content engagement, module demand, banner performance, and recent CMS updates for this touchscreen venue.</p>
          </div>
          <TimeRangeSelector
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            canShowAdvanced={canShowAdvanced}
          />
        </div>

        <div className="space-y-6">
          {error && (
            <div className="rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
              Failed to load touchscreen analytics: {error}
            </div>
          )}
          <TsVenueStatTiles
            loading={loading}
            trend={summary.trend}
            topContent={summary.topContent}
            moduleBreakdown={summary.moduleBreakdown}
            bannerBreakdown={summary.bannerBreakdown}
          />
          <TsVenueTrendDashboard loading={loading} trendData={summary.trend} />
          <div className="grid gap-6 xl:grid-cols-2">
            <TsVenueModuleBreakdownDashboard loading={loading} moduleBreakdown={summary.moduleBreakdown} />
            <TsVenueTopContentDashboard loading={loading} topContent={summary.topContent} />
          </div>
          <TsVenueBannerPerformanceDashboard loading={loading} bannerBreakdown={summary.bannerBreakdown} />
          <RecentChangesDashboard />
        </div>
      </SectionCard>
    </div>
  );
};

export default TsVenueAnalytics;
